'use client';

import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ApiError } from '@/lib/utils/api';
import type { Meter } from '@/hooks/use-meters';

type Draft = Record<string, string | number | boolean | null>;

function toNumberOrNull(val: Draft[string]): number | null {
	if (val == null || val === '') return null;
	const n = Number(val);
	return Number.isFinite(n) ? n : null;
}

/**
 * Local edit buffer for a single meter. Reads fall through to the live meter
 * row; only changed fields are sent on save (PATCH /api/meters/:id).
 *
 * `onRegisterSave` hands the page this draft's own `save` so its top-level
 * "Salva" can call it too: this draft lives only inside the tab, so without
 * it the page's "Salva" would save everything else and leave this behind
 * (INB-142). "Annulla" instead genuinely discards it — it navigates away, and
 * an unmounted tab's local draft state is gone with it, same as any cancel
 * button.
 */
export function useMeterDraft(
	meter: Meter | undefined,
	onSave: (id: string, patch: Record<string, unknown>) => Promise<unknown>,
	numericKeys: ReadonlySet<string> = new Set(),
	onRegisterSave?: (save: () => Promise<boolean>) => void,
	/** Dice alla pagina se la bozza ha modifiche: uscire senza salvare le perderebbe. */
	onDirtyChange?: (dirty: boolean) => void,
) {
	const [draft, setDraft] = useState<Draft>({});
	const [saving, setSaving] = useState(false);
	const dirty = Object.keys(draft).length > 0;

	const str = (k: keyof Meter) => {
		if (k in draft) return String(draft[k as string] ?? '');
		const val = meter?.[k];
		return val == null ? '' : String(val);
	};
	const bool = (k: keyof Meter) => {
		if (k in draft) return Boolean(draft[k as string]);
		return Boolean(meter?.[k]);
	};
	const set = (k: keyof Meter, v: string | number | boolean | null) =>
		setDraft((d) => ({ ...d, [k as string]: v }));

	/** Esito booleano: chi orchestra più salvataggi insieme deve sapere se fermarsi. */
	async function save(): Promise<boolean> {
		if (!meter || !dirty) return true;
		setSaving(true);
		try {
			const patch: Record<string, unknown> = {};
			for (const [k, val] of Object.entries(draft)) {
				// Un campo numerico svuotato è `null`, non 0: per la potenza di un modulo o
				// di una batteria 0 non è un valore, e il database lo rifiuta.
				patch[k] = numericKeys.has(k) ? toNumberOrNull(val) : val;
			}
			await onSave(meter.id, patch);
			setDraft({});
			return true;
		} catch (err) {
			// Senza questo, un salvataggio fallito (es. il 400 di "Potenza impianto
			// mancante") lasciava solo il pulsante che smetteva di girare — nessun
			// errore visibile, e la bozza restava buona ma sembrava scartata (INB-142).
			toast.error(err instanceof ApiError ? err.message : 'Errore nel salvataggio del misuratore');
			return false;
		} finally {
			setSaving(false);
		}
	}

	// Registrato a ogni render: `save` chiude sempre sull'ultima bozza, e la
	// pagina deve poterlo invocare dal proprio "Salva" senza tenerla essa stessa
	// sincronizzata con lo stato interno di questo hook.
	const onRegisterSaveRef = useRef(onRegisterSave);
	onRegisterSaveRef.current = onRegisterSave;
	useEffect(() => {
		onRegisterSaveRef.current?.(save);
	});
	// Un tab smontato non deve lasciare alla pagina un salvataggio agganciato a
	// una bozza congelata: sostituirlo con un no-op evita di riscrivere dati
	// vecchi se il misuratore torna a esistere sotto lo stesso riferimento.
	useEffect(() => () => onRegisterSaveRef.current?.(async () => true), []);

	const onDirtyChangeRef = useRef(onDirtyChange);
	onDirtyChangeRef.current = onDirtyChange;
	useEffect(() => {
		onDirtyChangeRef.current?.(dirty);
	}, [dirty]);
	useEffect(() => () => onDirtyChangeRef.current?.(false), []);

	return { meter, str, bool, set, dirty, saving, save };
}
